import { useLayoutEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameDay,
  startOfDay
} from 'date-fns';
import clsx from 'clsx';

import { api } from '../api';
import type { Calendar, CalendarEvent } from '../types';
import { eventStartDay } from '../event-day';
import { retintEventBackground, useResolvedTheme } from '../theme';

interface Props {
  calendar: Calendar;
  onClose: () => void;
  onPickEvent: (id: string, rect: DOMRect) => void;
}

const WINDOW_PAST_DAYS = 30;
const WINDOW_FUTURE_DAYS = 60;
const TODAY_SCROLL_RATIO = 0.25;

export default function CalendarAgenda({ calendar, onClose, onPickEvent }: Props) {
  const theme = useResolvedTheme();
  const today = startOfDay(new Date());
  const from = addDays(today, -WINDOW_PAST_DAYS);
  const to = addDays(today, WINDOW_FUTURE_DAYS);

  const scrollRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLElement>(null);

  const eventsQ = useQuery({
    queryKey: ['agenda', calendar.id, from.toISOString(), to.toISOString()],
    queryFn: () =>
      api.get<CalendarEvent[]>(
        `/api/events?from=${encodeURIComponent(from.toISOString())}` +
          `&to=${encodeURIComponent(to.toISOString())}` +
          `&calendar_id=${calendar.id}`
      ),
    refetchInterval: 60_000
  });

  const groups = useMemo(() => buildGroups(eventsQ.data ?? []), [eventsQ.data]);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const todayEl = todayRef.current;
    if (!container) return;
    if (!todayEl) {
      // No "Today" header rendered (e.g. no events on/after today). Scroll
      // to the bottom of past content so the most recent past day is visible.
      container.scrollTop = container.scrollHeight;
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const todayRect = todayEl.getBoundingClientRect();
    const offset = container.scrollTop + (todayRect.top - containerRect.top);
    container.scrollTop = Math.max(0, offset - container.clientHeight * TODAY_SCROLL_RATIO);
  }, [groups, calendar.id]);

  return (
    <aside className="fixed left-[240px] top-0 z-20 flex h-dvh w-[320px] flex-col border-r border-[var(--border)] bg-[var(--bg-sunken)] shadow-xl">
      <header className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-3">
        <span
          className="block h-[12px] w-[12px] flex-shrink-0 rounded-[3px]"
          style={{ background: retintEventBackground(calendar.background_color, theme) }}
        />
        <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold">
          {calendar.summary}
        </div>
        <button
          onClick={onClose}
          aria-label="close"
          className="rounded p-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
        >
          ‹
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2">
        {eventsQ.isLoading && (
          <div className="px-1 py-2 text-sm text-[var(--text-faint)]">Loading…</div>
        )}
        {eventsQ.isError && (
          <div className="px-1 py-2 text-sm text-[var(--text-faint)]">
            failed to load: {(eventsQ.error as Error).message}
          </div>
        )}
        {!eventsQ.isLoading && groups.length === 0 && (
          <div className="px-1 py-2 text-sm text-[var(--text-faint)]">No events in this range.</div>
        )}
        {groups.map((g) => (
          <section
            key={g.key}
            ref={g.isToday ? todayRef : undefined}
            className={clsx('mb-3', g.isPast && 'opacity-50')}
          >
            <header className="flex items-baseline gap-2 px-1 pb-1 pt-2 text-[0.78rem] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
              <span className={clsx(g.isToday && 'text-[var(--today-fg)]')}>{g.label}</span>
              <span className="text-[0.72rem] font-normal text-[var(--text-faint)]">
                {g.events.length}
              </span>
            </header>
            <ul className="flex flex-col gap-[2px]">
              {g.events.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={(ev) => {
                      const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                      onPickEvent(e.id, rect);
                    }}
                    className="agenda-row flex w-full flex-col gap-[2px] rounded-r py-[6px] pl-[10px] pr-2 text-left hover:bg-[var(--bg-hover)]"
                    style={
                      {
                        '--event-color': retintEventBackground(e.background_color, theme)
                      } as React.CSSProperties
                    }
                  >
                    <span className="text-[0.85rem] leading-snug">
                      {e.summary || '(no title)'}
                    </span>
                    <span className="text-[0.72rem] text-[var(--text-muted)]">
                      {eventTimeLabel(e)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </aside>
  );
}

type Group = {
  key: string;
  label: string;
  isToday: boolean;
  isPast: boolean;
  events: CalendarEvent[];
};

function buildGroups(events: CalendarEvent[]): Group[] {
  const today = startOfDay(new Date());
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const start = eventStartDay(e);
    if (!start) continue;
    const k = format(start, 'yyyy-MM-dd');
    const list = map.get(k) ?? [];
    list.push(e);
    map.set(k, list);
  }
  const entries = [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([k, list]) => {
    const d = new Date(`${k}T00:00:00`);
    const days = differenceInCalendarDays(d, today);
    const label =
      days === 0
        ? 'Today'
        : days === 1
          ? 'Tomorrow'
          : days === -1
            ? 'Yesterday'
            : days > 0 && days < 7
              ? format(d, 'EEEE')
              : format(d, 'EEE MMM d');
    return {
      key: k,
      label,
      isToday: isSameDay(d, today),
      isPast: days < 0,
      events: list.sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''))
    };
  });
}

function eventTimeLabel(e: CalendarEvent): string {
  if (e.all_day) return 'All day';
  if (!e.start_time) return '';
  const start = new Date(e.start_time);
  const end = e.end_time ? new Date(e.end_time) : null;
  const s = format(start, 'h:mma').toLowerCase();
  if (!end) return s;
  const t = format(end, 'h:mma').toLowerCase();
  const durMin = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const hours = Math.floor(durMin / 60);
  const mins = durMin % 60;
  const dur = hours > 0 ? `${hours}h${mins > 0 ? ` ${mins}m` : ''}` : `${mins}m`;
  return `${s}–${t} · ${dur}`;
}
